import {
  UseMutateFunction,
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';
import { useSession } from '../SessionProvider';
import {
  deleteFilter,
  Filter,
  getFilters,
  IdFilter,
  saveFilter,
  setFilters,
} from './filterEndpoints';

export const useGetFilters = (): UseQueryResult<Filter[] | null> => {
  const { olSession } = useSession();

  return useQuery(
    ['getFilters'],
    () => {
      return getFilters(olSession);
    },
    {
      staleTime: 30000,
      refetchInterval: false,
      refetchOnWindowFocus: false,
    }
  );
};

export type useSaveFiltersType = UseMutationResult<
  IdFilter,
  Error,
  Filter,
  unknown
>;

export type FilterMutation = UseMutateFunction<
  IdFilter,
  Error,
  Filter,
  unknown
>;

export const useSaveFilters = (): useSaveFiltersType => {
  const { olSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    (filter: Filter) => {
      return saveFilter(olSession, filter);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getFilters']);
      },
    }
  );
};

export const useDeleteFilters = (): UseMutationResult<
  null,
  Error,
  string,
  unknown
> => {
  const { olSession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    (uuid: string) => {
      return deleteFilter(olSession, uuid);
    },
    {
      onSuccess: () => queryClient.invalidateQueries(['getFilters']),
    }
  );
};

export const useSetFilters = (
  isReplayPlaying = false
): UseMutationResult<null, Error, string, unknown> => {
  const { olSession, replaySession } = useSession();

  return useMutation((filterExpression: string) => {
    if (replaySession && isReplayPlaying) {
      return setFilters(replaySession, filterExpression);
    }

    return setFilters(olSession, filterExpression);
  });
};
